/**
 * Smart Merge Logic for LinkedIn Skills Updates
 * 
 * This utility handles merging LinkedIn-imported skills with manually added skills
 * while preserving user customizations and tracking skill sources.
 */

export const mergeSkills = (existingSkills, linkedInSkills) => {
  const mergedSkills = new Map();
  const now = new Date();

  // Step 1: Process existing skills
  existingSkills.forEach(skill => {
    const key = skill.skill_name.toLowerCase().trim();
    mergedSkills.set(key, {
      ...skill,
      action: 'keep' // Default action
    });
  });

  // Step 2: Process LinkedIn skills
  linkedInSkills.forEach(linkedInSkill => {
    const key = linkedInSkill.name.toLowerCase().trim();
    const existingSkill = mergedSkills.get(key);

    if (existingSkill) {
      // Skill exists - update if it's a LinkedIn skill
      if (existingSkill.source === 'linkedin') {
        mergedSkills.set(key, {
          ...existingSkill,
          is_endorsed: linkedInSkill.is_endorsed || false,
          endorsement_count: linkedInSkill.endorsement_count || 0,
          last_linkedin_sync: now,
          is_active: true, // Reactivate if it was soft-deleted
          action: 'update'
        });
      } else {
        // Manual skill with same name as LinkedIn skill
        // Keep the manual one but note the conflict
        mergedSkills.set(key, {
          ...existingSkill,
          action: 'conflict',
          linkedin_equivalent: linkedInSkill
        });
      }
    } else {
      // New LinkedIn skill
      mergedSkills.set(key, {
        skill_name: linkedInSkill.name,
        source: 'linkedin',
        linkedin_skill_id: linkedInSkill.id || null,
        is_endorsed: linkedInSkill.is_endorsed || false,
        endorsement_count: linkedInSkill.endorsement_count || 0,
        is_active: true,
        last_linkedin_sync: now,
        action: 'add'
      });
    }
  });

  // Step 3: Mark LinkedIn skills that are no longer in the import
  const linkedInSkillNames = new Set(
    linkedInSkills.map(s => s.name.toLowerCase().trim())
  );

  mergedSkills.forEach((skill, key) => {
    if (skill.source === 'linkedin' && !linkedInSkillNames.has(key)) {
      // LinkedIn skill was removed from profile
      skill.is_active = false; // Soft delete
      skill.action = 'deactivate';
    }
  });

  return Array.from(mergedSkills.values());
};

/**
 * Filter skills for display
 */
export const getActiveSkills = (skills) => {
  return skills.filter(skill => skill.is_active);
};

/**
 * Group skills by source for UI display
 */
export const groupSkillsBySource = (skills) => {
  const activeSkills = getActiveSkills(skills);
  
  return {
    linkedin: activeSkills.filter(s => s.source === 'linkedin'),
    manual: activeSkills.filter(s => s.source === 'manual'),
    all: activeSkills
  };
};

/**
 * Check if a skill name already exists (case-insensitive)
 */
export const skillExists = (skills, newSkillName) => {
  const normalizedName = newSkillName.toLowerCase().trim();
  return skills.some(
    skill => skill.skill_name.toLowerCase().trim() === normalizedName && skill.is_active
  );
};

/**
 * Prepare skills for API submission
 */
export const prepareSkillsForSave = (skills) => {
  return skills.map(skill => ({
    skill_name: skill.skill_name.trim(),
    source: skill.source || 'manual',
    linkedin_skill_id: skill.linkedin_skill_id || null,
    is_endorsed: skill.is_endorsed || false,
    endorsement_count: skill.endorsement_count || 0,
    is_active: skill.is_active !== false // Default to true
  }));
};